/**
 * @packageDocumentation
 * @category Component
 */

'use client';

import 'BomdisoftComponents/inputs/segmented/styles.css';
import 'BomdisoftComponents/inputs/switch/styles.css';
import { joinClasses } from 'BomdisoftComponents/html/index';
import type { SwitchColor, SwitchSize } from 'BomdisoftComponents/inputs/switch/render';
import { getLanguageFromLocale, switchOff, switchOn } from '@bomdisoft/translations';
import { useParams } from 'next/navigation';
import { type ComponentProps, type ReactNode, useId } from 'react';

export type { SwitchColor, SwitchSize } from 'BomdisoftComponents/inputs/switch/render';

/** `size` is omitted alongside `type` because the native input's own `size` is a
 * numeric text-field width, meaningless on a checkbox, and it would intersect
 * this component's scale prop down to `never`. */
export type SwitchProps = Omit<ComponentProps<'input'>, 'size' | 'type'> & {
  color?: SwitchColor;
  label?: ReactNode;
  size?: SwitchSize;
};

/**
 * A two state control that names both of its states, approved 2026-08-19 as a
 * segmented pair rather than a track and knob, and unified 2026-08-20 with
 * `inputs/toggle-group` onto one segmented treatment: a switch is that control
 * with two options, a toggle group is the same control with more.
 *
 * The native checkbox remains the control and keeps `role='switch'`, so every
 * consumer's `onChange` still receives a real change event and assistive tech
 * still hears a switch. The two faces are decoration: they are `aria-hidden`
 * because the input already announces its own state, and repeating it in text
 * would double-announce.
 */
export const Switch = ({ className, color = 'primary', label, size = 'small', ...rest }: SwitchProps) => {
  const params = useParams();
  const language = getLanguageFromLocale(params?.lang as string) ?? 'english';
  const generatedId = useId();
  const inputId = rest.id ?? generatedId;
  const checked = rest.checked ?? false;
  const faceClassName = 'bomdisoft-segmented-segment bomdisoft-switch-face';
  const control = (
    <span className={joinClasses('bomdisoft-segmented', 'bomdisoft-switch', !label && className)} data-disabled={rest.disabled ? '' : undefined} data-tone={color}>
      <input {...rest} aria-checked={rest.checked} className='bomdisoft-switch-input' id={inputId} role='switch' type='checkbox' />
      <span aria-hidden='true' className={faceClassName} data-selected={checked ? '' : undefined} data-size={size} data-state='on'>
        {switchOn[language]}
      </span>
      <span aria-hidden='true' className={faceClassName} data-selected={checked ? undefined : ''} data-size={size} data-state='off'>
        {switchOff[language]}
      </span>
    </span>
  );
  if (!label) return control;
  return (
    <label className={joinClasses('bomdisoft-switch-label', className)} data-disabled={rest.disabled ? '' : undefined} htmlFor={inputId}>
      {control}
      {label}
    </label>
  );
};
